import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { meApi } from "@/api/endpoints/me";
import { useSession } from "@/contexts/SessionContext";
import { useToast } from "@/contexts/ToastContext";
import { FormError } from "@/components/FormError";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

export function ProfilePage(): JSX.Element {
  const { me } = useSession();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [section, setSection] = useState<"profile" | "password" | "email" | "sessions">("profile");

  const profileForm = useForm<{ display_name: string }>({
    defaultValues: { display_name: me?.display_name ?? "" },
    values: { display_name: me?.display_name ?? "" },
  });
  const passwordForm = useForm<{ current_password: string; new_password: string }>({});
  const emailForm = useForm<{ new_email: string }>({});

  const profileMut = useMutation({
    mutationFn: (v: { display_name: string }) => meApi.updateProfile(v),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.push("success", "Profile updated.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const passwordMut = useMutation({
    mutationFn: meApi.changePassword,
    onSuccess: () => {
      passwordForm.reset();
      toast.push("success", "Password changed. Other sessions were signed out.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const emailMut = useMutation({
    mutationFn: meApi.changeEmail,
    onSuccess: () => {
      emailForm.reset();
      toast.push("success", "Verification email sent to the new address.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  return (
    <div className="page" data-testid="profile-page">
      <div className="page-head">
        <div>
          <div className="page-title">Your profile</div>
          <div className="page-sub">Manage how you appear in the workspace and sign-in options.</div>
        </div>
      </div>

      <div className="tabs">
        {(["profile", "password", "email", "sessions"] as const).map((s) => (
          <button
            key={s}
            className={"tab" + (section === s ? " active" : "")}
            onClick={() => setSection(s)}
            data-testid={`profile-tab-${s}`}
          >
            {s === "profile"
              ? "Profile"
              : s === "password"
                ? "Password"
                : s === "email"
                  ? "Email"
                  : "Sessions"}
          </button>
        ))}
      </div>

      {section === "profile" ? (
        <div className="card" style={{ padding: "var(--s-5)", maxWidth: 480 }}>
          <form
            onSubmit={profileForm.handleSubmit((v) => profileMut.mutate(v))}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div className="field-group">
              <label>Email</label>
              <input className="field-input mono" value={me?.email ?? ""} disabled readOnly />
            </div>
            <div className="field-group">
              <label>Display name</label>
              <input
                className="field-input"
                data-testid="profile-display-name"
                {...profileForm.register("display_name")}
              />
            </div>
            <div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={profileMut.isPending}
                data-testid="profile-save"
              >
                {profileMut.isPending ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {section === "password" ? (
        <div className="card" style={{ padding: "var(--s-5)", maxWidth: 480 }}>
          <form
            onSubmit={passwordForm.handleSubmit((v) => passwordMut.mutate(v))}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div className="field-group">
              <label>Current password</label>
              <input
                type="password"
                className="field-input"
                data-testid="profile-current-password"
                {...passwordForm.register("current_password", { required: "Required." })}
              />
              <FormError message={passwordForm.formState.errors.current_password?.message} />
            </div>
            <div className="field-group">
              <label>New password</label>
              <input
                type="password"
                className="field-input"
                data-testid="profile-new-password"
                {...passwordForm.register("new_password", {
                  minLength: { value: 12, message: "At least 12 characters." },
                  required: "Required.",
                })}
              />
              <FormError message={passwordForm.formState.errors.new_password?.message} />
            </div>
            <div>
              <button type="submit" className="btn btn-primary" disabled={passwordMut.isPending}>
                {passwordMut.isPending ? "Updating…" : "Change password"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {section === "email" ? (
        <div className="card" style={{ padding: "var(--s-5)", maxWidth: 480 }}>
          <form
            onSubmit={emailForm.handleSubmit((v) => emailMut.mutate(v))}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div className="field-group">
              <label>Current email</label>
              <input className="field-input mono" value={me?.email ?? ""} disabled readOnly />
            </div>
            <div className="field-group">
              <label>New email</label>
              <input
                type="email"
                className="field-input"
                data-testid="profile-new-email"
                {...emailForm.register("new_email", { required: "Required." })}
              />
              <span className="help">
                We will send a verification link to confirm your new address.
              </span>
            </div>
            <div>
              <button type="submit" className="btn btn-primary" disabled={emailMut.isPending}>
                {emailMut.isPending ? "Sending…" : "Send verification"}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {section === "sessions" ? (
        <div className="card" style={{ maxWidth: 600 }}>
          <div className="empty">
            Active sessions are visible here once detailed session management ships in WP-B.
          </div>
        </div>
      ) : null}
    </div>
  );
}
